import { useState } from 'react';
import { api, type TodoItem, type TodoSweep, type TodoSweepDecision } from '../../lib/api';
import { Button } from '../ui';
import { TodoFacts, type ClosedTodos } from './TodoList';
import { NO_CONTACT, wordsOf } from './todoFormat';

const NOT_SAVED = 'Not saved - try again.';

interface Props {
  sweep: TodoSweep;
  /** Re-reads the list after every write: the server decides what is left and when the sweep is finished. */
  onChanged: () => Promise<void>;
  /**
   * ⭐ R33: to-dos closed from their LIST row since the last read. Their cards are skipped and "N left" counts without
   * them; an Undo on the row takes one out again and its card comes back.
   */
  closed: ClosedTodos;
}

/**
 * The first sweep (his choice, spec 4.1): the last 30 days, 10 at a time - Done, Still open, Drop - before reminders
 * start. ⛔ Nothing happens until he taps: starting it is a button, never a page load.
 * ⭐ R17 / R31: "Start reminders" is there from the first view - before the clean-up starts as well as during it - to
 * skip the rest. The page shows his whole open list below this either way.
 */
export default function SweepCards({ sweep, onChanged, closed }: Props) {
  const cards = sweep.next.filter((t) => !closed[t.ghl_task_id]);
  // Only the shown batch is known to be pending; one closed beyond it is counted off at the next read.
  const left = sweep.pending - (sweep.next.length - cards.length);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const act = async (key: string, write: () => Promise<unknown>) => {
    setBusy(key);
    setErrors(({ [key]: _gone, ...rest }) => rest);
    try {
      await write();
    } catch (e) {
      setErrors((er) => ({ ...er, [key]: wordsOf(e, NOT_SAVED) }));
      setBusy(null);
      return;
    }
    await onChanged();
    setBusy(null);
  };
  const decide = (t: TodoItem, decision: TodoSweepDecision) =>
    void act(t.ghl_task_id, () => api.todos.sweep(t.ghl_task_id, decision));
  const startReminders = (
    <Button variant="outline" onClick={() => void act('finish', api.todos.finishSweep)} isLoading={busy === 'finish'}>
      Start reminders
    </Button>
  );
  const finishError = errors.finish && <p role="alert" className="mt-2 text-sm text-red-700">{errors.finish}</p>;

  if (!sweep.started) {
    return (
      <section className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">Clean-up first</h2>
        <p className="mt-1 text-sm text-gray-600">
          Your tasks from the last 30 days, 10 at a time: Done, Still open or Drop. Reminders start when you finish,
          or tap Start reminders to skip it.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button onClick={() => void act('start', api.todos.startSweep)} isLoading={busy === 'start'}>
            Start the clean-up
          </Button>
          {startReminders}
        </div>
        {errors.start && <p role="alert" className="mt-2 text-sm text-red-700">{errors.start}</p>}
        {finishError}
      </section>
    );
  }

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Clean-up</h2>
          <p className="text-sm text-gray-600">{`${left} left`}</p>
        </div>
        {startReminders}
      </div>
      {finishError}
      {cards.length > 0 ? (
        <ul aria-label="Clean-up" className="mt-4 space-y-3">
          {cards.map((t) => (
            <li key={t.ghl_task_id} className="rounded-lg border border-gray-200 p-4">
              <TodoFacts todo={t} />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {/* ⭐ R14: no contact, no Done or Drop - GoHighLevel would refuse them; Still open is the app's own. */}
                {t.ghl_contact_id !== null && (
                  <Button size="sm" disabled={busy !== null} onClick={() => decide(t, 'done')}>Done</Button>
                )}
                <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => decide(t, 'open')}>Still open</Button>
                {t.ghl_contact_id !== null ? (
                  <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => decide(t, 'dropped')}>Drop</Button>
                ) : (
                  <span className="text-xs text-gray-600">{NO_CONTACT}</span>
                )}
              </div>
              {errors[t.ghl_task_id] && <p role="alert" className="mt-2 text-sm text-red-700">{errors[t.ghl_task_id]}</p>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-gray-600">{left > 0 ? 'Tap Refresh for the next ones.' : 'Nothing left to clean up.'}</p>
      )}
    </section>
  );
}
